import { Card, CardTitle, MiniBar } from '@/components/ui'
import { formatCurrency, formatCompactNumber } from '@/utils/formatters'
import { useT } from '@/i18n/useT'

/**
 * The dashboard's two charts, drawn the way the design mockup does: plain
 * flex/grid bars on the token palette rather than a charting library, so they
 * follow the light/dark theme without a per-series colour list. Exact values
 * stay one hover away via the native tooltip.
 */

export interface MonthlyBar {
  /** Short month label, e.g. "Jan" */
  month: string
  value: number
}

export function MonthlyGrossChart({
  data,
  isLoading,
  currentMonthIndex,
}: {
  data: MonthlyBar[]
  isLoading?: boolean
  /** Index into `data` to highlight in the full accent colour */
  currentMonthIndex?: number
}) {
  const { t } = useT()
  const max = Math.max(...data.map((d) => d.value), 1)

  return (
    <Card style={{ flex: '2 1 520px', minWidth: 0 }}>
      <CardTitle note={`EUR · ${t('booked invoices')}`}>{t('Monthly gross volume')}</CardTitle>
      {isLoading ? (
        <div style={{ height: 200, marginTop: 20, background: 'var(--hover)', borderRadius: 8 }} />
      ) : (
        <div
          style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 200, marginTop: 20 }}
        >
          {data.map((d, i) => {
            const filled = d.value > 0
            return (
              <div
                key={d.month}
                title={`${t(d.month)}: ${formatCurrency(d.value)}`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  height: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>
                  {filled ? formatCompactNumber(d.value) : ''}
                </div>
                <div
                  style={{
                    width: '100%',
                    borderRadius: '5px 5px 0 0',
                    background: filled
                      ? i === currentMonthIndex
                        ? 'var(--accent)'
                        : 'var(--accentBorder)'
                      : 'var(--line2)',
                    height: filled ? `${Math.round((d.value / max) * 100)}%` : 3,
                  }}
                />
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t(d.month)}</div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

export interface SupplierBar {
  supplierName: string
  value: number
}

export function TopSuppliersChart({
  data,
  isLoading,
}: {
  data: SupplierBar[]
  isLoading?: boolean
}) {
  const { t } = useT()
  const max = Math.max(...data.map((d) => d.value), 1)

  return (
    <Card style={{ flex: '1 1 320px', minWidth: 0 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{t('Top suppliers')}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
        {t('Share of gross spend')}
      </div>
      {isLoading ? (
        <div style={{ height: 180, marginTop: 18, background: 'var(--hover)', borderRadius: 8 }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
          {data.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>{t('No data.')}</div>
          )}
          {data.map((d) => (
            <div key={d.supplierName} title={`${d.supplierName}: ${formatCurrency(d.value)}`}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {d.supplierName}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {formatCompactNumber(d.value)} €
                </div>
              </div>
              <div style={{ marginTop: 7 }}>
                <MiniBar fraction={d.value / max} height={7} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
